import { useState, useEffect, useRef, useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import { formatOfferingLabel } from '../utils/offeringLabel';
import type { UserProfile } from '../types/user';
import { useLessons } from '../contexts/LessonContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { useIsMobile } from '../contexts/ResponsiveContext';
import { getUserProfile } from '../services/userProfile';
import { getLatestDraft, saveVideoEditDraft } from '../services/videoEditDraft';
import type { VideoEditorDraftData } from './LessonVideoEditor';
const LessonVideoEditor = lazy(() => import('./LessonVideoEditor').then(m => ({ default: m.LessonVideoEditor })));
import { LessonEditPreview } from './LessonEditPreview';
import LessonReviewBlock from './LessonReviewBlock';
import { useCoachHasMessaged } from '../hooks/useCoachHasMessaged';
import type { OriginalVideo, VideoEditDraft, VideoContext } from '../types/videoEdit';
import BackButton from './BackButton';
import PlayerProfileModal from './PlayerProfileModal';
import { AttachedDrills } from './AttachedDrills';
import DetailSection from './DetailSection';
import LessonStatusIcon from './LessonStatusIcon';
import { BTN_PRIMARY, BTN_SECONDARY_ON_PAGE } from './buttonStyles';
import { formatLessonDate } from '../utils/lessonDate';
import ButtonSpinner from './ButtonSpinner';
import { busyStyle } from '../utils/busyStyle';
import { ChartLineUp } from '@phosphor-icons/react';

interface CoachLessonDetailProps {
  order: Order;
  lesson: Lesson;
  onBack?: () => void;
  onMessage?: () => void;
  /** 'page' (default) is the full-screen route treatment: its own back button
   *  and title block. 'panel' fills a container that already supplies both —
   *  the Orders right pane and the Messages LessonOrderPanel. Everything else
   *  is identical between the two. */
  variant?: 'page' | 'panel';
}

// Resolve a real player name. Same logic as CoachLessonCard — rejects the
// literal "player"/"Player" sentinel from seed data and falls back through
// the joined playerProfile → email local-part → generic. Console-warns on
// fallback so missing-displayName cases stay visible during dev.
function resolvePlayerName(order: Order, playerProfile: UserProfile | null): string {
  const candidates = [
    playerProfile?.displayName,
    playerProfile?.playerProfile?.displayName,
    order.playerName,
  ];
  for (const c of candidates) {
    const s = c?.trim();
    if (s && s.toLowerCase() !== 'player') return s;
  }
  const email = playerProfile?.email;
  if (email) {
    const localPart = email.split('@')[0];
    if (localPart) {
      // eslint-disable-next-line no-console
      console.warn('[CoachLessonDetail] Player has no displayName; falling back to email local-part:', email);
      return localPart;
    }
  }
  // eslint-disable-next-line no-console
  console.warn('[CoachLessonDetail] Player has no displayName or email — using generic fallback', { playerId: order.playerId });
  return 'Tennis Player';
}

function playerInitial(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed[0].toUpperCase() : '?';
}


export default function CoachLessonDetail({ order, lesson, onBack, onMessage, variant = 'page' }: CoachLessonDetailProps) {
  const isPanel = variant === 'panel';
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    submitting,
    markLessonHasDraft,
    submitLessonDraft: contextSubmitLessonDraft,
    getOrCreateOriginalVideo: contextGetOrCreateOriginalVideo,
  } = useLessons();
  const { showError, showSuccess } = useToast();
  const isMobile = useIsMobile();

  const [playerProfile, setPlayerProfile] = useState<UserProfile | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showVideoEditor, setShowVideoEditor] = useState(false);

  /* The player's submission video, so we can stop it when the coach leaves.
     It had no ref at all, which is why its audio followed them out. */
  const submissionVideoRef = useRef<HTMLVideoElement>(null);

  // Stable identity across renders — same fix as LessonDeliveredDetail:
  // LessonEditPreview's load effect keys on this object, and an inline
  // literal re-fired it on every parent re-render, tearing the breakdown
  // player down to its loading spinner.
  const previewContext = useMemo(
    () => ({ type: 'lesson' as const, id: lesson.id }),
    [lesson.id],
  );

  /* Leaving route 1: the editor opens over this screen.
     LessonVideoEditor is a full-screen OVERLAY rendered as a sibling, not a
     navigation — this component stays mounted underneath it, video and all.
     So the unmount cleanup below never fires for Continue Editing or Record
     Lesson, and this flag is what covers them.

     Keyed on the flag rather than wired into the two buttons' handlers, so a
     third way of opening the editor is covered without anyone remembering. */
  useEffect(() => {
    if (!showVideoEditor) return;
    // pause() is idempotent; no need to check whether it is playing.
    submissionVideoRef.current?.pause();
  }, [showVideoEditor]);

  /* Leaving route 2: everything else. Back button, a bottom-nav tab, the
     Message button, a coach or player profile link, selecting a different
     lesson in the list beside this pane — all of them unmount this component,
     and all of them used to let the audio carry on into wherever the coach
     landed. One cleanup covers every one of them, including routes that do not
     exist yet.

     Removing a <video> from the DOM does not reliably stop it; the element can
     keep decoding until it is collected, which is long enough to be heard. The
     explicit pause is what makes it silent.

     Deliberately no resume. Same reasoning as the modal pause: the coach has
     moved on, and a video restarting itself when they come back — possibly
     with sound, from a position they left minutes ago — is a surprise rather
     than a convenience. */
  useEffect(() => {
    const video = submissionVideoRef.current;
    return () => { video?.pause(); };
  }, []);
  const [originalVideo, setOriginalVideo] = useState<OriginalVideo | null>(null);
  const [existingDraft, setExistingDraft] = useState<VideoEditDraft | null>(null);
  const [loadedDraftData, setLoadedDraftData] = useState<VideoEditorDraftData | null>(null);
  const [localSubmitted, setLocalSubmitted] = useState(false);

  const lessonContext: VideoContext = useMemo(() => ({ type: 'lesson', id: lesson.id }), [lesson.id]);

  const hasDraftEdits = existingDraft && (
    existingDraft.voiceoverNotableSegmentClips.length > 0 ||
    (existingDraft.notes && existingDraft.notes.trim().length > 0)
  );

  useEffect(() => {
    if (lesson.playerId) {
      getUserProfile(lesson.playerId).then(setPlayerProfile).catch(() => {});
    }
  }, [lesson.playerId]);

  // Pre-load draft when lesson is in_progress
  useEffect(() => {
    if (!currentUser || lesson.status !== 'in_progress' || !lesson.playerVideoStoragePath) return;
    let cancelled = false;
    (async () => {
      try {
        const video = await contextGetOrCreateOriginalVideo(lesson);
        if (cancelled) return;
        setOriginalVideo(video);
        if (video) {
          const draft = await getLatestDraft(lessonContext, video.id, currentUser.uid);
          if (!cancelled) setExistingDraft(draft);
        }
      } catch (err) {
        console.error('Error preloading draft:', err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.id, lesson.status]);

  const handleStartLesson = async () => {
    setLoadedDraftData(null);
    setShowVideoEditor(true);

    let video = originalVideo;
    if (!video && lesson.playerVideoStoragePath && currentUser) {
      try {
        video = await contextGetOrCreateOriginalVideo(lesson);
        setOriginalVideo(video);
      } catch (err) {
        console.error('Error creating original video:', err);
        return;
      }
    }

    let currentDraft = existingDraft;
    if (video && currentUser) {
      try {
        currentDraft = await getLatestDraft(lessonContext, video.id, currentUser.uid);
        if (currentDraft) setExistingDraft(currentDraft);
      } catch {
        currentDraft = existingDraft;
      }
    }

    if (currentDraft) {
      setLoadedDraftData({
        notableSegments: currentDraft.notableSegments ?? [],
        voiceoverNotableSegmentClips: currentDraft.voiceoverNotableSegmentClips ?? [],
        notes: currentDraft.notes,
        audioMuted: currentDraft.audioMuted,
        coachNotes: currentDraft.coachNotes,
        videoRotation: currentDraft.videoRotation,
        attachedDrills: currentDraft.attachedDrills ?? [],
      });
    }
  };

  const handleSaveDraft = async (data: VideoEditorDraftData): Promise<VideoEditorDraftData> => {
    if (!currentUser) throw new Error('No user available');
    let video = originalVideo;
    if (!video && lesson.playerVideoStoragePath) {
      video = await contextGetOrCreateOriginalVideo(lesson);
      setOriginalVideo(video);
    }
    if (!video) throw new Error('Could not create video document');

    const savedDraft = await saveVideoEditDraft({
      userId: currentUser.uid,
      userName: currentUser.displayName || 'Coach',
      context: lessonContext,
      originalVideoId: video.id,
      audioMuted: data.audioMuted,
      notes: data.notes,
      existingDraftId: existingDraft?.id,
      notableSegments: data.notableSegments,
      voiceoverNotableSegmentClips: data.voiceoverNotableSegmentClips,
      coachNotes: data.coachNotes,
      attachedDrills: data.attachedDrills,
      videoRotation: data.videoRotation,
    });
    setExistingDraft(savedDraft);
    markLessonHasDraft(lesson.id);

    const storedClipMap = new Map(savedDraft.voiceoverNotableSegmentClips.map((c) => [c.id, c]));
    return {
      ...data,
      voiceoverNotableSegmentClips: data.voiceoverNotableSegmentClips.map((clip) => {
        const stored = storedClipMap.get(clip.id);
        return stored
          ? { ...clip, videoStoragePath: stored.videoStoragePath, videoDuration: stored.videoDuration }
          : clip;
      }),
    };
  };

  // Reset localSubmitted once Firestore confirms the lesson is no longer in_progress
  useEffect(() => {
    if (localSubmitted && lesson.status !== 'in_progress') {
      setLocalSubmitted(false);
    }
  }, [lesson.status, localSubmitted]);

  // The mobile header (z-50) and bottom nav (z-9999) sit outside <main overflow-x-hidden>,
  // which creates a Chrome stacking context that traps LVE's fixed positioning. Hide both
  // while LVE is open so they don't intercept pointer events on the editor toolbar.
  useEffect(() => {
    const nav = document.getElementById('mobile-bottom-nav');
    const header = document.getElementById('mobile-header');
    if (showVideoEditor) {
      nav?.style.setProperty('display', 'none', 'important');
      header?.style.setProperty('display', 'none', 'important');
    } else {
      nav?.style.removeProperty('display');
      header?.style.removeProperty('display');
    }
    return () => {
      nav?.style.removeProperty('display');
      header?.style.removeProperty('display');
    };
  }, [showVideoEditor]);

  const handleSubmitDraft = async (data: VideoEditorDraftData) => {
    if (!currentUser || submitting || localSubmitted) return;
    if (data.voiceoverNotableSegmentClips.length === 0) {
      showError('Please add at least one lesson clip before submitting.');
      return;
    }
    setLocalSubmitted(true);

    const processingInfo = {
      playerName: resolvePlayerName(order, playerProfile),
      lessonTitle: order.offeringSnapshot?.title || (order.type === 'shot_consult' ? 'Shot Consultation' : 'Lesson'),
      lessonId: lesson.id,
    };

    /* No sessionStorage write. It was the "page-refresh recovery" that could
       not survive the one event worth recovering from, and having a second
       source of truth is what let a phantom banner outlive a real submission.
       submissionStatus on the lesson document replaces it.

       The event below stays: it is a head start for the indicator while the
       intent write is still in flight, not a record of anything. */
    window.dispatchEvent(new CustomEvent('coachLessonStatus', {
      detail: { type: 'coach_lesson_processing_start', ...processingInfo },
    }));

    // Fire-and-forget: the context provider persists across navigation
    const statusCallback = (_msg: string) => { /* indicator handles UI */ };
    contextSubmitLessonDraft(lesson, data, originalVideo, existingDraft?.id, statusCallback)
      .then(() => {
        window.dispatchEvent(new CustomEvent('coachLessonStatus', {
          detail: { type: 'coach_lesson_delivered' },
        }));
      })
      .catch((err) => {
        window.dispatchEvent(new CustomEvent('coachLessonStatus', {
          detail: { type: 'coach_lesson_failed', error: err?.message || 'Failed to deliver lesson' },
        }));
      });

    // Don't navigate or close the editor here — the editor shows a brief
    // "Sent" success state and then fires onSubmitSuccess, which handles
    // the nav + toast. Submission itself continues in the background.
  };

  const handleMessage = onMessage ?? (() => navigate(`/inbox/${order.conversationId}`));

  const playerName = resolvePlayerName(order, playerProfile);
  const playerFirstName = playerName.split(' ')[0];
  // Live profile takes priority; order snapshot is the fallback so the
  // avatar updates immediately after a player re-uploads their photo.
  const playerAvatarUrl = playerProfile?.playerProfile?.avatarUrl
    || playerProfile?.avatarUrl
    || order.playerAvatarUrl
    || undefined;
  const utr = (playerProfile?.playerProfile as unknown as Record<string, unknown> | undefined)?.utr as number | undefined
    ?? (playerProfile as unknown as Record<string, unknown> | undefined)?.utr as number | undefined;

  const isConsult = order.type === 'shot_consult';
  const typeLabel = isConsult ? 'Shot Consult' : 'Single Lesson';
  const submittedDate = formatLessonDate(lesson.updatedAt ?? lesson.createdAt);

  /* lesson first, order second — the same precedence the player's own side
     uses. A note written at booking lands on the order; one written when the
     video is uploaded lands on the lesson, and this block read only the order,
     so an upload-time note was invisible to the coach on their main lesson
     screen. */
  const playerNote = (lesson.playerMessage ?? order.playerMessage ?? '').trim();

  const isInProgress = lesson.status === 'in_progress';
  const isDelivered = lesson.status === 'delivered';
  // Mapped keys only. A dynamic string like `Submitted ${date}` never matches
  // STATUS_ICON and silently falls through to the bare fallback ring, which is
  // exactly what the list rows do today.
  const paneStatusLabel = isDelivered
    ? 'Delivered'
    : isInProgress
      ? 'Awaiting your review'
      : 'Awaiting upload';
  /**
   * The date that used to sit in the name row, now trailing the status.
   *
   * Which date, and what to call it, depends on the state. A delivered lesson
   * dates from delivery, not from `updatedAt`, and calling that "Submitted"
   * would put the wrong word on the wrong timestamp. A lesson awaiting upload
   * has no submission to date at all, so it carries no date rather than
   * labelling its booking date as a submission.
   */
  const statusMeta = isDelivered
    ? formatLessonDate(lesson.deliveredAt)
    : isInProgress && submittedDate
      ? `Submitted ${submittedDate}`
      : '';

  /* Lifted out of the JSX so it can be handed to LessonReviewBlock, which
     renders it at the top of its card. Moved verbatim rather than rebuilt
     from fields: it carries two profile-opening buttons, the status line
     and its icon, the UTR badge and the parent-account suffix, and every
     one of those survives the move because the markup itself moved. Only
     the row's own page margins came off, since the card supplies them. */
  /* The 48 avatar against a two-line stack, so the avatar has something its own
     height to sit beside. It once carried a single 14px name with the status
     hanging underneath the whole row, which left the avatar oversized for its
     content and the status orphaned. */
  /* Only while it is the coach's move, and only until they have written. */
  const coachHasMessaged = useCoachHasMessaged(order.conversationId, order.coachId);
  const showMessageNudge = isInProgress && coachHasMessaged === false;

  const playerIdentityRow = (
    <div className="flex items-start gap-3">
      {/* A second target on the same handler, so tapping the photo still
          opens the profile. It cannot share the name's button: that one
          sits inside the column, and wrapping both would take the status
          line in with them. aria-hidden because the name button beside it
          already carries the accessible label for this action. */}
      <button
        type="button"
        onClick={() => setShowProfileModal(true)}
        tabIndex={-1}
        aria-hidden="true"
        /* Oasis, not biscayne: every other identity row in the app fills its
           initials circle with #54C3EA, and biscayne is for active and
           selected. */
        style={{ background: '#54C3EA' }}
        className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden text-[16px] font-bold text-white flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
      >
        {playerAvatarUrl
          ? <img src={playerAvatarUrl} alt={playerName} className="w-full h-full object-cover" />
          : playerInitial(playerName)
        }
      </button>

      <div className="min-w-0 flex-1">
        {/* The name is the whole tap target. The status line beneath is
            information, not a control, and used to open the profile too. */}
        <button
          type="button"
          onClick={() => setShowProfileModal(true)}
          className="block max-w-full truncate text-left text-[17px] leading-[22px] font-semibold text-[#1C1C1C] cursor-pointer hover:opacity-90 transition-opacity"
          aria-label={`View ${playerName}'s profile`}
        >
          {playerName}
          {order.playerAccountType === 'parent' && order.playerParentFirstName && (
            <span className="ml-1.5 text-[13px] font-normal text-db-text-3">
              (Parent: {order.playerParentFirstName})
            </span>
          )}
        </button>

        {/* Status, on the same icon-plus-label the list rows use, so one
            state has one name and one mark across the screen. */}
        {/* 6, not 2. The column is then 22 + 6 + 18 = 46, still inside the
            48 the avatar sets, so the row height is unchanged. 9 would be
            the first value to grow it. */}
        {/* Nothing truncates because the screen is narrow, and
                nothing is dropped either. It reflows: below md the date takes
                its own line under the status. A coach needs to know how long a
                player has been waiting, so the date is not the thing to lose.
            The separator goes with the wrap: a "·" leading a line is an orphan.
            The column is 22 + 6 + 18 + 18 = 64 when it stacks, past the 48 the
            avatar sets, so the row grows on mobile and the avatar stays top
            aligned rather than re-centring. */}
        <div className="flex flex-col md:flex-row md:items-center md:gap-[6px] text-[13px] leading-[18px] min-w-0" style={{ marginTop: 6 }}>
          <span className="flex items-center gap-[6px] min-w-0">
            <LessonStatusIcon label={paneStatusLabel} />
            <span className="shrink-0 font-semibold text-[#1C1C1C]">{paneStatusLabel}</span>
          </span>
          {statusMeta && (
            <span className="truncate font-normal text-[rgba(60,60,67,0.6)] pl-5 md:pl-0">
              <span className="hidden md:inline">· </span>{statusMeta}
            </span>
          )}
        </div>
      </div>

      {utr != null && (
        /* CL-1's icon-plus-text, the same shape the coaches grid uses.
           items-center on the row centres it against the two-line stack. */
        <span className="flex flex-shrink-0 items-center gap-1 whitespace-nowrap text-[13px] leading-[18px] text-[#1C1C1C]">
          <ChartLineUp size={14} color="#54C3EA" weight="bold" aria-hidden="true" />
          UTR {utr}
        </span>
      )}
    </div>
  );

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-db-bg">
      <div className={`w-full mx-auto max-w-[700px] px-4 py-6 pt-5 md:px-8 md:py-7 md:pt-7 ${isMobile ? 'pb-[calc(32px+env(safe-area-inset-bottom,0px))]' : ''}`}>

        {/* Transfer alert banner */}
        {lesson.isTransferred && (
          <div className="flex gap-3 items-start rounded-[12px] px-5 py-5 mb-5" style={{ background: '#FFF9E6', border: '1.5px solid #E8D48A' }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-[1px] bg-biscayne">
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2.5">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-extrabold mb-[5px] text-db-text-1">Player transferred to you by Double Bagel</div>
              <div className="text-[13px] leading-[1.65] text-db-text-2">
                {lesson.transferNote
                  ? lesson.transferNote
                  : "This player's previous coach is no longer available. We've matched them with you based on your coaching style and availability. The player has been notified you're their new coach."}
              </div>
              {lesson.transferredFromCoachName && (
                <div className="text-[12px] mt-1 text-db-text-2">
                  Previous coach: <span className="font-semibold">{lesson.transferredFromCoachName}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Header — mirrors the player detail: the lesson type is the title and
            the player identity drops to the meta row beneath. Back sits inline
            with the title; the panel variant's container supplies its own. */}
        {/* The ROW is sticky, not the button. This component is its own scroll
            container (overflow-y-auto on the root), so sticky resolves against
            that box rather than the page, and Layout's overflow change never
            reached here. The button alone could not stick either: its parent
            was a 36px row, and a sticky element cannot travel outside its
            parent. bg-db-bg so content does not show through underneath.

            The back button is in normal flow at every width now. It used to be
            absolute below md so the title could centre in the full width; the
            title is left-aligned on both breakpoints instead, which is what
            pins cleanly. */}
        <div className={`${isPanel ? '' : 'sticky top-0 z-30 bg-db-bg'} flex items-center justify-start gap-3 min-h-[36px] py-2`}>
          {!isPanel && <BackButton onClick={onBack} fallback="/orders" flush />}
          <h1 className="text-[20px] md:text-[26px] font-bold text-[#1C1C1C] text-left">{typeLabel}</h1>
        </div>

        {/* Who the player is and what they said, in one card. It renders
            whether or not a review exists: the identity row inside it is the
            only thing naming the player on this screen, so it cannot be gated
            on one. The review half appears when there is one. */}
        <LessonReviewBlock
          lessonId={lesson.id}
          viewer="coach"
          className="mt-3 mb-6"
          identity={playerIdentityRow}
        />

        {/* One line, no control of its own. The Message button in the identity
            row above is the action — a link here would be the second entry
            point to messaging that this was not allowed to add. It appears only
            while a lesson is awaiting the coach's review AND the thread has no
            note from them, so it goes away by being acted on rather than by
            being dismissed. */}
        {showMessageNudge && (
          <p className="text-[13px] leading-[18px] text-db-text-2" style={{ marginTop: -12, marginBottom: 24 }}>
            {playerFirstName} has not heard from you yet. Let them know when to expect the breakdown.
          </p>
        )}


        {/* Hero video — full content-width with editorial rounded corners.
            Capped at 70vh so portrait videos don't blow up the layout. The
            The label above is section 8.2's header, the same one DetailSection
            draws and the same one the player pane uses for its video labels. */}
        {lesson.playerVideoUrl && (
          <div style={{ marginTop: 32 }}>
            <p className="text-[13px] leading-[18px] font-semibold uppercase text-[rgba(60,60,67,0.6)]" style={{ letterSpacing: '0.5px', marginBottom: 16 }}>Player&apos;s submission</p>
            <video
              ref={submissionVideoRef}
              src={lesson.playerVideoUrl}
              controls
              controlsList="nodownload"
              preload="metadata"
              className="w-full rounded-[14px] bg-black"
              style={{ maxHeight: '70vh' }}
              /* No crossOrigin: display-only. CORS-mode loading fails on
                 iPhone Safari against our bucket (Content-Range not exposed)
                 — see the same removal on LessonDeliveredDetail. */
            />
          </div>
        )}

        {/* Player's note — the same tinted block the player side uses for its
            own note, so both halves of the exchange read as a pair. */}
        {playerNote && (
          <DetailSection className="mt-5" header="Note from player">
            <p className="whitespace-pre-wrap text-[15px] leading-[20px] text-[#1C1C1C]">
              {playerNote}
            </p>
          </DetailSection>
        )}

        {/* Coach's breakdown video (delivered state only). Same 8.2 header as
            every other section on this pane. */}
        {isDelivered && lesson.publishedDraftId && lesson.publishedOriginalVideoId && (
          <div style={{ marginTop: 32 }}>
            <p className="text-[13px] leading-[18px] font-semibold uppercase text-[rgba(60,60,67,0.6)]" style={{ letterSpacing: '0.5px', marginBottom: 16 }}>Your breakdown</p>
            <LessonEditPreview
              offeringLabel={order ? formatOfferingLabel({ offeringType: order.type, lessonNumber: lesson.lessonNumber, totalLessons: order.totalLessons }) : undefined}
              deliveredAt={lesson.deliveredAt}
              context={previewContext}
              originalVideoId={lesson.publishedOriginalVideoId}
              /* lesson.coachName overrides the order's, per its own type
                 comment. The coach sees their own name here, which is the same
                 title the player sees on the same lesson. */
              coachName={lesson.coachName ?? order.coachName}
              draftId={lesson.publishedDraftId}
              showControls
              downloadUrl={lesson.coachVideoUrl}
              downloadStoragePath={lesson.coachVideoStoragePath}
            />
          </div>
        )}

        {/* Coach's delivered notes — the same DetailSection as the player's
            note above, so both halves of the exchange read as a pair. mt-5
            clears the clip metadata LessonPreview renders under the breakdown
            video; mb-6 keeps the existing gap to Practice Drills. */}
        {isDelivered && lesson.coachNotes && (
          <DetailSection className="mt-5 mb-6" header="Your notes">
            <p className="whitespace-pre-wrap text-[15px] leading-[20px] text-[#1C1C1C]">
              {lesson.coachNotes}
            </p>
          </DetailSection>
        )}

        {/* Attached Drills */}
        {isDelivered && lesson.attachedDrills && lesson.attachedDrills.length > 0 && (
          <div className="mb-6">
            <AttachedDrills drills={lesson.attachedDrills} title="Practice Drills" />
          </div>
        )}

        {/* CTAs — single inline block on both mobile and desktop. Sits in
            normal page flow at the bottom of the page content, scrolling
            with the page (not pinned). `justify-end` keeps Message right-
            aligned. When in-progress, Record Lesson takes flex-1 and
            dominates; Message tucks compactly on the right. When delivered,
            Message is the only button and sits alone on the right. */}
        {!showVideoEditor && (
          <div
            className={`flex flex-col md:flex-row md:items-center gap-2.5 md:gap-3 ${
              // Alone on the delivered state, so it hugs its content and centres
              // rather than filling the column. A lone full-width button reads
              // as the primary action, which a secondary is not.
              isInProgress ? 'md:justify-start' : 'items-center md:justify-center'
            }`}
            style={{ marginTop: 24 }}
          >
            {isInProgress && (
              <button
                data-testid="start-lesson-btn"
                onClick={handleStartLesson}
                disabled={submitting || localSubmitted}
                aria-busy={submitting}
                style={busyStyle(submitting)}
                className={`${BTN_PRIMARY} md:w-auto md:px-7`}
              >
                {/* 8.3b: the spinner replaces the label, the copy does not
                    change. The old grey-fill "Submitted" state was the button
                    telling the user what they had just done. */}
                {submitting
                  ? <ButtonSpinner />
                  : localSubmitted
                    ? 'Submitted'
                    : (hasDraftEdits ? 'Continue Editing' : 'Record Lesson')}
              </button>
            )}

            {/* Beside Record Lesson it matches its height and radius, stacked
                full width below md. Alone it hugs its content at 20 either
                side, on both breakpoints. */}
            <button
              onClick={handleMessage}
              /* On the page background, not inside a section, so it takes the
                 lifted variant. */
              className={`${BTN_SECONDARY_ON_PAGE} flex-shrink-0 whitespace-nowrap ${
                isInProgress ? 'md:w-auto md:px-5' : 'w-auto px-5'
              }`}
            >
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Message {playerFirstName}
            </button>
          </div>
        )}

      </div>

      {/* Full-screen video editor overlay — lazy-loaded; only pulled in when the coach opens it */}
      {showVideoEditor && lesson.playerVideoUrl && (
        <Suspense fallback={null}>
        <LessonVideoEditor
          lessonRequest={{ ...lesson, playerVideoUrl: lesson.playerVideoUrl }}
          onCancel={() => { setShowVideoEditor(false); setLoadedDraftData(null); }}
          onSaveDraft={handleSaveDraft}
          onSubmitDraft={handleSubmitDraft}
          initialDraft={loadedDraftData || undefined}
          playerId={lesson.playerId}
          playerName={playerName}
          playerFirstName={playerFirstName}
          playerAvatarUrl={playerAvatarUrl}
          playerUtr={utr}
          lessonId={lesson.id}
          originalVideoId={originalVideo?.id}
          offeringType={order.type}
          onSubmitSuccess={() => {
            setShowVideoEditor(false);
            setLoadedDraftData(null);
            /* "Sent", not "delivered". This fires from onSubmitSuccess, which
               is the editor's handoff moment — the upload has barely started,
               so claiming delivery here contradicted the pill still reading
               "Sending" beside it. Delivery is announced by the indicator,
               which is driven by the real submitting -> delivered transition
               on the lesson document.
               Not "processing" either: the composition job that follows feeds
               My Cloud and the native app, not the player's watch page, so
               calling it processing implies the lesson is not ready when it
               is. */
            showSuccess(`Sent to ${playerFirstName}`, 4000);
            navigate('/orders?tab=delivered');
          }}
        />
        </Suspense>
      )}
      {showProfileModal && (
        <PlayerProfileModal
          playerId={order.playerId}
          seedName={playerName}
          seedAvatarUrl={playerAvatarUrl}
          onClose={() => setShowProfileModal(false)}
        />
      )}
    </div>
  );
}
